// Thanks to https://github.com/react-hookz/web/blob/master/src/useMediaQuery/index.ts
// Docs: https://react-hookz.github.io/web/?path=/docs/sensor-usemediaquery--example
import { useEffect, useState } from "react";

const queriesMap = new Map();

const createQueryEntry = (query) => {
	const mql = matchMedia(query);
	const dispatchers = new Set();
	const listener = () => {
		for (const d of dispatchers) {
			d(mql.matches);
		}
	};

	mql.addEventListener("change", listener, { passive: true });

	return {
		mql,
		dispatchers,
		listener,
	};
};

const querySubscribe = (query, setState) => {
	let entry = queriesMap.get(query);

	if (!entry) {
		entry = createQueryEntry(query);
		queriesMap.set(query, entry);
	}

	entry.dispatchers.add(setState);
	setState(entry.mql.matches);
};

const queryUnsubscribe = (query, setState) => {
	const entry = queriesMap.get(query);

	if (entry) {
		const { mql, dispatchers, listener } = entry;
		dispatchers.delete(setState);

		if (dispatchers.size === 0) {
			queriesMap.delete(query);

			if (mql.removeEventListener) {
				mql.removeEventListener("change", listener);
			} else {
				mql.removeListener(listener);
			}
		}
	}
};

/**
 * Tracks the state of CSS media query.
 *
 * @param {string} query - CSS media query to track.
 * @param {Object} options - Hook options
 * @param {boolean} options.initializeWithValue - Determine media query match state on first render.
 * Setting this to true will cause hydration mismatch errors with SSR since server
 * returns undefined but client returns the actual match.
 * Defaults to false for SSR compatibility.
 * @returns {boolean|undefined} Media query match state
 */
export function useMediaQuery(query, options = {}) {
	const { initializeWithValue = false } = options;

	const [state, setState] = useState(() => {
		if (initializeWithValue && typeof window !== "undefined") {
			let entry = queriesMap.get(query);
			if (!entry) {
				entry = createQueryEntry(query);
				queriesMap.set(query, entry);
			}

			return entry.mql.matches;
		}
		return undefined;
	});

	useEffect(() => {
		if (typeof window !== "undefined") {
			querySubscribe(query, setState);

			return () => {
				queryUnsubscribe(query, setState);
			};
		}
	}, [query]);

	return state;
}
